/**
 * Incident report, step by step: What happened → Where & when → People → Property → Narrative → Review.
 * Saves as you go. Only the questions an offense needs are asked. The Review step lists anything missing in
 * plain words, each with a button that jumps right to it.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ageOn,
  checkReport,
  ANIMAL_CRUELTY_ACTIVITY,
  ARREST_TYPES,
  ARRESTEE_ARMED,
  EYE_COLORS,
  EVIDENCE_STATUS_LABEL,
  HAIR_COLORS,
  JUVENILE_DISPOSITION,
  newArrest,
  ASSAULT_CIRCUMSTANCES,
  BIAS,
  CATEGORY_LABELS,
  COMMON_LOCATIONS,
  CRIMINAL_ACTIVITY,
  DRUG_TYPES,
  DRUG_UNITS,
  ETHNICITIES,
  EXCEPTIONAL_CLEARANCE,
  INJURIES,
  isSuspect,
  isVictim,
  joinName,
  nameParts,
  splitName,
  JUSTIFIABLE_CIRCUMSTANCES,
  JUSTIFIABLE_DETAIL,
  GANG_INFO,
  SPECIAL_CIRCUMSTANCES,
  LEOKA_ACTIVITY,
  LEOKA_ASSIGNMENT,
  DRUG_ARREST_ACTIVITY,
  ARREST_DRUG_TYPES,
  isOrgVictim,
  victimOffenses,
  offensesOfVictim,
  OFFICER_VICTIM_OFFENSES,
  labelOf,
  LOCATION_TYPES,
  LOSS_TYPES,
  NEGLIGENT_CIRCUMSTANCES,
  needsFor,
  newOffense,
  NARCAN_FORM_URL,
  narcanRelevant,
  NARCAN_GIVERS,
  newPerson,
  newProperty,
  OFFENSE_BY_CODE,
  OFFENSES,
  personLabel,
  PROPERTY_TYPES,
  RACES,
  RELATIONSHIPS,
  searchOffenses,
  SEXES,
  SUSPECTED_USING,
  VEHICLE_PROPERTY,
  VICTIM_TYPES,
  WEAPONS,
  INFO_ONLY,
  narrativeHints,
  hintApplied,
  type NarrativeHint,
  type IncidentReport,
  type PersonRole,
  type ReportIssue,
  type ReportOffense,
  type ReportPerson,
  type ReportProperty,
  type ReportStep,
} from '@civilos/domain';
import { api, ApiError } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDateTime, fmtStamp } from '../shared/format.ts';
import { chargeWords, StatutePicker } from '../shared/StatutePicker.tsx';
import { LicenseScanButton } from '../shared/LicenseScan.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { photoToJpegBase64 } from '../shared/setout.tsx';
import { PhotoFolders } from '../ops/Photos.tsx';

type Step = ReportStep | 'review';
const STEPS: { id: Step; label: string }[] = [
  { id: 'what', label: 'What happened' },
  { id: 'where', label: 'Location and time' },
  { id: 'people', label: 'People' },
  { id: 'property', label: 'Property' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'review', label: 'Review & send' },
];
const STATUS_LABEL: Record<string, string> = { draft: 'Draft', submitted: 'Awaiting approval', returned: 'Returned for corrections', approved: 'Approved' };
const EVENT_LABEL: Record<string, string> = { created: 'Created', submitted: 'Submitted', returned: 'Returned', approved: 'Approved', discarded: 'Discarded', supplement: 'Supplement added' };
const STATUS_TONE: Record<string, 'info' | 'warn' | 'ok' | 'neutral'> = { draft: 'neutral', submitted: 'info', returned: 'warn', approved: 'ok' };

const uid = (p: string) => `${p}${Math.random().toString(36).slice(2, 9)}`;
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
export const evidenceBase = () => (location.pathname.startsWith('/field') ? '/field/evidence' : '/evidence');
export const reportsBase = () => (location.pathname.startsWith('/field') ? '/field/reports' : '/reports');

// ─── Small building blocks ──────────────────────────────────────────────────

type Opt = { code: string; label: string };

/** Pick one: big tappable buttons. */
function Choice({ label, options, value, onChange, allowClear }: { label: string; options: Opt[]; value: string | null; onChange: (v: string | null) => void; allowClear?: boolean }) {
  return (
    <div className="rw-q">
      <div className="rw-ql">{label}</div>
      <div className="rw-choices" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.code} type="button" role="radio" aria-checked={value === o.code} className={value === o.code ? 'on' : ''} onClick={() => onChange(allowClear && value === o.code ? null : o.code)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Pick several (up to max). */
function Multi({ label, options, value, onChange, max = 3, hint }: { label: string; options: Opt[]; value: string[]; onChange: (v: string[]) => void; max?: number; hint?: string }) {
  return (
    <div className="rw-q">
      <div className="rw-ql">
        {label} {hint && <span className="hint">{hint}</span>}
      </div>
      <div className="rw-choices" role="group" aria-label={label}>
        {options.map((o) => {
          const on = value.includes(o.code);
          return (
            <button
              key={o.code}
              type="button"
              aria-pressed={on}
              className={on ? 'on' : ''}
              onClick={() => onChange(on ? value.filter((x) => x !== o.code) : max === 1 ? [o.code] : value.length >= max ? [...value.slice(1), o.code] : [...value, o.code])}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Txt({ label, value, onChange, placeholder, type = 'text', wide, inputMode }: { label: string; value: string | number | null; onChange: (v: string) => void; placeholder?: string; type?: string; wide?: boolean; inputMode?: 'numeric' | 'decimal' | 'tel' }) {
  return (
    <label className={`rw-f${wide ? ' wide' : ''}`}>
      <span>{label}</span>
      <input type={type} value={value ?? ''} placeholder={placeholder} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Sel({ label, options, value, onChange, empty = 'Choose…' }: { label: string; options: Opt[]; value: string | null; onChange: (v: string | null) => void; empty?: string }) {
  return (
    <label className="rw-f">
      <span>{label}</span>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">{empty}</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Card({ id, title, onRemove, children, tone }: { id: string; title: ReactNode; onRemove?: () => void; children: ReactNode; tone?: string }) {
  return (
    <section id={`ref-${id}`} className={`rw-card${tone ? ` ${tone}` : ''}`}>
      <header>
        <h3>{title}</h3>
        {onRemove && (
          <button type="button" className="btn sm ghost" onClick={onRemove} aria-label="Remove">
            Remove
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

// ─── Step 1: What happened ──────────────────────────────────────────────────

function WhatStep({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const [q, setQ] = useState('');
  const [browse, setBrowse] = useState(false);
  const found = useMemo(() => searchOffenses(q, 6), [q]);
  const add = (code: string) => {
    if (r.offenses.some((o) => o.code === code)) return;
    set((x) => void x.offenses.push(newOffense(uid('o'), code)));
    setQ('');
    setBrowse(false);
  };
  const upd = (id: string, f: (o: ReportOffense) => void) => set((x) => f(x.offenses.find((o) => o.id === id)!));
  const groups = useMemo(() => {
    const g = new Map<string, typeof OFFENSES>();
    for (const o of OFFENSES) g.set(o.category, [...(g.get(o.category) ?? []), o]);
    return [...g];
  }, []);
  const infoOnly = r.offenses.some((o) => o.code === INFO_ONLY);
  const crimes = r.offenses.filter((o) => o.code !== INFO_ONLY);
  const setInfoOnly = (on: boolean) => {
    if (on && crimes.length && !window.confirm(`Make this an information-only report? The ${crimes.length === 1 ? 'offense' : `${crimes.length} offenses`} you picked will come off.`)) return;
    set((x) => void (x.offenses = on ? [newOffense(uid('o'), INFO_ONLY)] : x.offenses.filter((o) => o.code !== INFO_ONLY)));
  };
  return (
    <div className="stack">
      <label className={`check rw-infoonly${infoOnly ? ' on' : ''}`}>
        <input type="checkbox" checked={infoOnly} onChange={(e) => setInfoOnly(e.target.checked)} />
        <span>
          <strong>Information only — no crime, no charges</strong>
          <span className="small muted"> A record of what happened (civil matter, found property, a call that wasn’t a crime). No victim or suspect needed.</span>
        </span>
      </label>
      {!infoOnly && (
      <div className="rw-search">
        <label htmlFor="rw-what" className="rw-big">
          What happened?
          <span className="hint"> Describe it in plain words, or type an offense code</span>
        </label>
        <input id="rw-what" autoFocus={!r.offenses.length} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. broke into shed, took a mower · fight at the bar · DUI · 220" autoComplete="off" />
        {found.length > 0 && (
          <ul className="rw-found" role="listbox" aria-label="Matching offenses">
            {found.map((o) => (
              <li key={o.code}>
                <button type="button" onClick={() => add(o.code)} disabled={r.offenses.some((x) => x.code === o.code)}>
                  <span className="rw-fc">{o.group === 'NC' ? '—' : o.code}</span>
                  <strong>{o.plain}</strong>
                  <span className="muted small rw-fn">{o.group === 'NC' ? 'Not a crime · records only' : `${o.name} · ${CATEGORY_LABELS[o.category]}`}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button type="button" className="btn link" onClick={() => setBrowse(!browse)}>
          {browse ? 'Hide the full list' : 'Or pick from the full list'}
        </button>
        {browse && (
          <div className="rw-browse">
            {groups.map(([cat, list]) => (
              <div key={cat}>
                <div className="rw-ql">{CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS]}</div>
                <div className="rw-choices">
                  {list.map((o) => (
                    <button key={o.code} type="button" onClick={() => add(o.code)} disabled={r.offenses.some((x) => x.code === o.code)}>
                      {o.plain}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {r.offenses.length === 0 && <p className="muted">Add everything that happened. A break-in where something was taken is two: burglary and theft.</p>}

      {r.offenses.map((o) => {
        const n = needsFor(o);
        const def = OFFENSE_BY_CODE.get(o.code);
        const nc = def?.group === 'NC';
        return (
          <Card key={o.id} id={o.id} title={<>{!nc && <span className="code-chip">{o.code}</span>}{def?.plain ?? o.code} {!nc && def && <span className="rw-official">{def.name}</span>}</>} onRemove={() => set((x) => void (x.offenses = x.offenses.filter((y) => y.id !== o.id)))}>
            {nc ? (
              <p className="muted small">Not a crime — kept in your records, not reported to the state.</p>
            ) : (
              <div className="stack sm">
                {n.askAttempted && (
                  <Choice label="Was it completed?" options={[{ code: 'c', label: 'Completed' }, { code: 'a', label: 'Attempted only' }]} value={o.attempted == null ? null : o.attempted ? 'a' : 'c'} onChange={(v) => upd(o.id, (x) => void (x.attempted = v == null ? null : v === 'a'))} />
                )}
                {n.weapon && (
                  <Multi
                    label="Weapon or force used"
                    hint="up to 3"
                    options={n.weaponChoices ? WEAPONS.filter((w) => n.weaponChoices!.includes(w.code)) : WEAPONS}
                    value={o.weapons}
                    onChange={(v) => upd(o.id, (x) => void (x.weapons = v))}
                  />
                )}
                {n.circumstances === 'assault' && (
                  <Multi
                    label="Circumstances"
                    hint="up to 2"
                    max={2}
                    options={o.code === '13A' ? ASSAULT_CIRCUMSTANCES.filter((c) => c.code !== '07') : ASSAULT_CIRCUMSTANCES}
                    value={o.circumstances}
                    onChange={(v) => upd(o.id, (x) => void (x.circumstances = only(v, '10')))}
                  />
                )}
                {(n.circumstances === 'negligent' || n.circumstances === 'justifiable') && (
                  <Choice
                    label="Circumstances"
                    options={n.circumstances === 'negligent' ? NEGLIGENT_CIRCUMSTANCES : JUSTIFIABLE_CIRCUMSTANCES}
                    value={o.circumstances[0] ?? null}
                    onChange={(v) => upd(o.id, (x) => void (x.circumstances = v ? [v] : []))}
                  />
                )}
                {n.circumstances === 'justifiable' && <Sel label="What happened?" options={JUSTIFIABLE_DETAIL} value={o.justifiable} onChange={(v) => upd(o.id, (x) => void (x.justifiable = v))} />}
                {n.activity && (
                  <Multi
                    label={n.activity === 'animal' ? 'Type of cruelty' : 'What were they doing?'}
                    options={n.activity === 'animal' ? ANIMAL_CRUELTY_ACTIVITY : CRIMINAL_ACTIVITY.filter((c) => c.code !== 'E' || ['370'].includes(o.code))}
                    value={o.criminalActivity}
                    onChange={(v) => upd(o.id, (x) => void (x.criminalActivity = v))}
                  />
                )}
                {n.entry && (
                  <Choice label="How did they get in?" options={[{ code: 'F', label: 'Forced (pried, kicked, broke)' }, { code: 'N', label: 'Not forced (unlocked, open)' }]} value={o.entry} onChange={(v) => upd(o.id, (x) => void (x.entry = v as 'F' | 'N' | null))} />
                )}
                {n.entry && ['14', '19'].includes(r.location.type ?? '') && (
                  <Txt label="How many rooms / units were entered?" type="number" inputMode="numeric" value={o.premises} onChange={(v) => upd(o.id, (x) => void (x.premises = v ? Number(v) : null))} />
                )}
                {def?.group === 'A' && (
                  <>
                    <Multi label="Was the suspect using…" options={SUSPECTED_USING} value={o.suspectedUsing} onChange={(v) => upd(o.id, (x) => void (x.suspectedUsing = only(v, 'N', o.suspectedUsing)))} />
                    <BiasField value={o.bias} onChange={(v) => upd(o.id, (x) => void (x.bias = v))} />
                  </>
                )}
                {n.gang && <Choice label="Gang involvement" options={GANG_INFO} value={o.gang} onChange={(v) => upd(o.id, (x) => void (x.gang = v))} />}
                {def?.group === 'A' && (
                  <details className="rw-more" open={o.special.length > 0}>
                    <summary>Special circumstances {o.special.length ? `(${o.special.length})` : '(bomb threat, carjacking, gas drive-off…)'}</summary>
                    <Multi
                      label="Special circumstances"
                      hint="up to 3; “Victim is a police officer” is required when the victim is an officer"
                      max={3}
                      options={SPECIAL_CIRCUMSTANCES.filter((c) => c.code !== 'C' || o.code === '120')}
                      value={o.special}
                      onChange={(v) => upd(o.id, (x) => void (x.special = v))}
                    />
                  </details>
                )}
              </div>
            )}
          </Card>
        );
      })}
      {r.offenses.some((o) => needsFor(o).cargo) && (
        <section className="rw-card">
          <header>
            <h3>Cargo theft?</h3>
          </header>
          <Choice
            label="Were the goods part of a commercial shipment (truck, rail, warehouse in the supply chain)?"
            options={[
              { code: 'N', label: 'No' },
              { code: 'Y', label: 'Yes, cargo theft' },
            ]}
            value={r.cargoTheft == null ? null : r.cargoTheft ? 'Y' : 'N'}
            onChange={(v) => set((x) => void (x.cargoTheft = v == null ? null : v === 'Y'))}
          />
        </section>
      )}
      {narcanRelevant(r) && <NarcanBox r={r} set={set} />}
    </div>
  );
}

/** "Pass on" this case to the next shift (worth mentioning — not every call is). */
function PassOnBox({ incidentId, passOn: initial }: { incidentId: string; passOn: any }) {
  const { me, can } = useAuth();
  const [passOn, setPassOn] = useState<any>(initial ?? null);
  const [on, setOn] = useState(false);
  const [note, setNote] = useState('');
  const a = useAction();
  if (passOn)
    return (
      <section className="rw-card passon-on">
        <header>
          <h3>On the pass-on</h3>
          {(passOn.created_by === me?.user.id || can('reports.approve')) && (
            <button type="button" className="btn sm ghost" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/passon/${passOn.id}/remove`), setPassOn(null)))}>
              Take it off
            </button>
          )}
        </header>
        <p>
          “{passOn.note}” <span className="small muted">— {passOn.by_name}, {fmtDateTime(passOn.created_at)}</span>
        </p>
        <ErrorBox error={a.error} />
      </section>
    );
  if (!can('reports.write') && !can('reports.approve')) return null;
  return (
    <section className="rw-card">
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} /> <strong>Add to pass-on</strong> <span className="small muted">— something the next shift should know (a pattern, a suspect, a car to watch for)</span>
      </label>
      {on && (
        <div className="row gap-sm wrap" style={{ marginTop: 8 }}>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. 3rd car break-in on Lancaster Hwy this week — watch for a dark pickup" maxLength={500} aria-label="What to pass on" style={{ flex: 1, minWidth: 260 }} />
          <button type="button" className="btn primary" disabled={a.busy || note.trim().length < 5} onClick={() => a.run(async () => { const x = await api.post<any>('/api/passon', { incidentId, note }); setPassOn(x.passOn); setOn(false); setNote(''); })}>
            Pass it on
          </button>
        </div>
      )}
      <ErrorBox error={a.error} />
    </section>
  );
}

/** Was Narcan used? If the deputy gave it: fill in the state Narcan use form and keep a copy. */
function NarcanBox({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const n = r.narcan ?? { used: null, givenBy: null, doses: null, formDone: false };
  const up = (f: (n: NonNullable<IncidentReport['narcan']>) => void) =>
    set((x) => {
      const cur = x.narcan ?? { used: null, givenBy: null, doses: null, formDone: false };
      f(cur);
      x.narcan = cur;
    });
  const drugs = r.offenses.some((o) => o.code === '35A' || o.code === '35B');
  return (
    <section className={`rw-card narcan${n.used ? ' on' : ''}`}>
      <header>
        <h3>Narcan</h3>
        {drugs && n.used == null && <span className="small muted">Drugs on this call</span>}
      </header>
      <label className="check narcan-q">
        <input type="checkbox" checked={!!n.used} onChange={(e) => up((m) => void ((m.used = e.target.checked), !e.target.checked && ((m.givenBy = null), (m.formDone = false), (m.doses = null))))} />
        <strong>Narcan (naloxone) was used on this call</strong>
      </label>
      {n.used && (
        <div className="stack sm">
          <Choice label="Who gave it?" options={NARCAN_GIVERS.map((g) => ({ code: g.code, label: g.label }))} value={n.givenBy} onChange={(v) => up((m) => void (m.givenBy = v as any))} />
          <label className="rw-f">
            <span>Doses</span>
            <input type="number" min={1} max={20} inputMode="numeric" value={n.doses ?? ''} onChange={(e) => up((m) => void (m.doses = e.target.value === '' ? null : Number(e.target.value)))} aria-label="Narcan doses" style={{ maxWidth: 120 }} />
          </label>
          {n.givenBy === 'me' ? (
            <div className="narcan-form">
              <p>
                <strong>You gave Narcan — fill in the Narcan use form.</strong> When you finish it, <strong>print it or take a screenshot</strong> of the completed form and keep it. Your supervisor will follow up to verify it was done.
              </p>
              <a className="btn primary" href={NARCAN_FORM_URL} target="_blank" rel="noopener noreferrer">
                Open the Narcan use form ↗
              </a>
              <label className="check">
                <input type="checkbox" checked={n.formDone} onChange={(e) => up((m) => void (m.formDone = e.target.checked))} /> I completed the Narcan form and kept a copy (printed or screenshot)
              </label>
            </div>
          ) : n.givenBy === 'deputy' ? (
            <p className="small">The deputy who gave it fills in their own Narcan use form. Their supervisor follows up.</p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** "None of these" / "Unknown" style choices stand alone: picking one clears the rest, and picking another clears it. */
function only(next: string[], solo: string, prev?: string[]): string[] {
  if (!next.includes(solo)) return next;
  const justPicked = !(prev ?? []).includes(solo);
  return justPicked || next.length === 1 ? [solo] : next.filter((x) => x !== solo);
}

/** Data Element 8A: no bias / not determined yet / a bias picked from the list. Nothing is picked until the deputy answers. */
function BiasField({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const mode = value == null ? null : value === '88' ? '88' : value === '99' ? '99' : 'B';
  const [open, setOpen] = useState(mode === 'B');
  return (
    <div className="stack sm">
      <Choice
        label="Was it a bias (hate) crime?"
        options={[
          { code: '88', label: 'No bias' },
          { code: '99', label: 'Not sure yet' },
          { code: 'B', label: 'Yes, bias motivated' },
        ]}
        value={open && mode == null ? 'B' : mode}
        onChange={(v) => {
          if (v === 'B') return setOpen(true);
          setOpen(false);
          onChange(v);
        }}
      />
      {(open || mode === 'B') && <Sel label="Bias against" options={BIAS.filter((b) => b.code !== '88' && b.code !== '99')} value={mode === 'B' ? value : null} onChange={(v) => onChange(v)} />}
    </div>
  );
}

// ─── Step 2: Where & when ───────────────────────────────────────────────────

/** A date box and a time box side by side (clearer than one combined picker), with an optional "now" button. */
export function WhenField({ label, value, onChange, now, hint }: { label: string; value: string | null; onChange: (iso: string | null) => void; now?: string; hint?: ReactNode }) {
  const local = toLocalInput(value);
  const [d, t] = local ? local.split('T') : ['', ''];
  const put = (date: string, time: string) => onChange(date ? fromLocalInput(`${date}T${time || '00:00'}`) : null);
  return (
    <div className="rw-when" role="group" aria-label={label}>
      <div className="rw-when-l">{label}</div>
      <div className="rw-when-row">
        <label className="rw-f">
          <span>Date</span>
          <input type="date" aria-label={`${label} date`} value={d} onChange={(e) => put(e.target.value, t ?? '')} />
        </label>
        <label className="rw-f">
          <span>Time</span>
          <input type="time" aria-label={`${label} time`} value={t ?? ''} onChange={(e) => put(d || toLocalInput(new Date().toISOString()).slice(0, 10), e.target.value)} />
        </label>
        {now && (
          <button type="button" className="btn rw-now" onClick={() => onChange(new Date().toISOString())}>
            {now}
          </button>
        )}
      </div>
      {hint && <div className="small muted">{hint}</div>}
    </div>
  );
}

/** When it happened: one time, or "sometime between" two times. */
export function HappenedFields({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const [between, setBetween] = useState(!!r.occurredTo);
  const unk = r.occurredUnknown;
  const setUnk = (k: 'date' | 'hour', on: boolean) => set((x) => void (x.occurredUnknown = on ? k : null));
  return (
    <div className="stack sm">
      {unk !== 'date' && <WhenField label={between ? 'Happened sometime after' : 'Happened'} value={r.occurredFrom} onChange={(v) => set((x) => void (x.occurredFrom = v))} now="It just happened" />}
      <div className="row gap wrap">
        <label className="rw-unk">
          <input type="checkbox" checked={unk === 'date'} onChange={(e) => setUnk('date', e.target.checked)} /> The date isn’t known (the report date is sent instead)
        </label>
        {unk !== 'date' && (
          <label className="rw-unk">
            <input type="checkbox" checked={unk === 'hour'} onChange={(e) => setUnk('hour', e.target.checked)} /> The day is known but not the time
          </label>
        )}
      </div>
      {unk === 'date' ? null : between ? (
        <>
          <WhenField label="And before" value={r.occurredTo} onChange={(v) => set((x) => void (x.occurredTo = v))} hint="The latest it could have happened, like when the victim got home." />
          <button type="button" className="btn link sm rw-between" onClick={() => (setBetween(false), set((x) => void (x.occurredTo = null)))}>
            I know the exact time
          </button>
        </>
      ) : (
        <button type="button" className="btn link sm rw-between" onClick={() => setBetween(true)}>
          Not sure exactly? It happened sometime between two times
        </button>
      )}
    </div>
  );
}

export function ReportedFields({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  return <WhenField label="Reported" value={r.reportedAt} onChange={(v) => set((x) => void (x.reportedAt = v))} now="Reported now" hint="When it was reported to the sheriff’s office (the call, or when they flagged you down)." />;
}

export function AddressFields({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const a = useAction();
  const [note, setNote] = useState<string | null>(null);
  const loc = r.location;
  const setLoc = (patch: Partial<IncidentReport['location']>) => set((x) => void Object.assign(x.location, patch));
  const here = () =>
    a.run(
      () =>
        new Promise<void>((resolve, reject) => {
          if (!navigator.geolocation) return reject(new Error('This device can’t share its location.'));
          navigator.geolocation.getCurrentPosition(
            async (p) => {
              try {
                const w = await api.get<any>(`/api/reports/where?lat=${p.coords.latitude.toFixed(6)}&lng=${p.coords.longitude.toFixed(6)}`);
                setLoc({ lat: w.lat, lng: w.lng, ...(w.line1 ? { line1: w.line1, city: w.city ?? loc.city, zip: w.zip ?? loc.zip } : {}) });
                setNote(w.source === 'county' ? `At ${w.line1} (county address point).` : w.line1 ? `Near ${w.line1}. Check the house number.` : 'Location saved. Type the address too.');
                resolve();
              } catch (e) {
                reject(e);
              }
            },
            () => reject(new Error('Couldn’t get your location. Type the address instead.')),
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
          );
        }),
    );
  const find = () =>
    a.run(async () => {
      const f = await api.post<any>('/api/reports/locate', { line1: loc.line1, city: loc.city, zip: loc.zip });
      setLoc({ lat: f.lat, lng: f.lng });
      setNote(f.source === 'county' ? `On the map: ${f.matched} (county address point).` : `On the map: ${f.matched ?? loc.line1}.`);
    });
  return (
    <div className="stack sm">
      <div className="row gap wrap">
        <button type="button" className="btn sm" onClick={here} disabled={a.busy}>
          📍 Use my location
        </button>
        <button type="button" className="btn sm" onClick={find} disabled={a.busy || !loc.line1.trim()}>
          Find on map
        </button>
        {loc.lat != null && <Badge tone="ok">On the map</Badge>}
        {note && <span className="small muted">{note}</span>}
      </div>
      <div className="rw-row">
        <Txt label="Address" wide value={loc.line1} onChange={(v) => setLoc({ line1: v, lat: null, lng: null })} placeholder="118 N Main St" />
        <Txt label="City" value={loc.city} onChange={(v) => setLoc({ city: v || null })} />
        <Txt label="Zip" value={loc.zip} inputMode="numeric" onChange={(v) => setLoc({ zip: v || null })} />
      </div>
      <Txt label="Exactly where (optional)" wide value={loc.detail} onChange={(v) => setLoc({ detail: v || null })} placeholder="Back porch, shed behind the house, pump 4…" />
      <ErrorBox error={a.error} />
    </div>
  );
}

/** What kind of place: the common ones as buttons, and a search across all of them. */
export function PlaceFields({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const [q, setQ] = useState('');
  const loc = r.location;
  const setType = (code: string) => set((x) => void (x.location.type = code));
  const common = LOCATION_TYPES.filter((l) => COMMON_LOCATIONS.includes(l.code));
  const chosen = loc.type ? LOCATION_TYPES.find((l) => l.code === loc.type) : undefined;
  const t = q.trim().toLowerCase();
  const words = t.split(/\s+/).filter(Boolean);
  const hits = t ? LOCATION_TYPES.filter((l) => words.every((w) => l.label.toLowerCase().includes(w))).slice(0, 12) : [];
  const shown = t ? hits : chosen && !common.some((c) => c.code === chosen.code) ? [...common, chosen] : common;
  return (
    <div className="stack sm">
      <label className="rw-f wide rw-place-q">
        <span>Search all kinds of places</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type: church, school, bank, woods, jail, dock…" aria-label="Search kinds of place" />
      </label>
      <div className="rw-choices big" role="radiogroup" aria-label="What kind of place">
        {shown.map((l) => (
          <button key={l.code} type="button" role="radio" aria-checked={loc.type === l.code} className={loc.type === l.code ? 'on' : ''} onClick={() => (setType(l.code), setQ(''))}>
            {l.label}
          </button>
        ))}
      </div>
      {t && hits.length === 0 && <p className="small muted">Nothing matches “{q.trim()}”. Try another word, or pick “Other / unknown”.</p>}
    </div>
  );
}

function WhereStep({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  return (
    <div className="stack">
      <section className="rw-card">
        <header>
          <h3>When did it happen?</h3>
        </header>
        <HappenedFields r={r} set={set} />
      </section>
      <section className="rw-card">
        <header>
          <h3>When was it reported?</h3>
        </header>
        <ReportedFields r={r} set={set} />
      </section>
      <section className="rw-card">
        <header>
          <h3>Where</h3>
        </header>
        <AddressFields r={r} set={set} />
      </section>
      <section className="rw-card">
        <header>
          <h3>What kind of place?</h3>
        </header>
        <PlaceFields r={r} set={set} />
      </section>
    </div>
  );
}

// ─── Step 3: People ─────────────────────────────────────────────────────────

const ROLE_LABELS: Record<PersonRole, string> = { victim: 'Victim', suspect: 'Suspect', arrestee: 'Arrested', witness: 'Witness', complainant: 'Reported it', other: 'Other' };

/** Last / First / Middle / Suffix, with a lookup of people already in records as you type. */
function NameFields({ p, onPart, onPick }: { p: ReportPerson; onPart: (k: 'last' | 'first' | 'middle' | 'suffix', v: string) => void; onPick: (h: any) => void }) {
  const parts = nameParts(p);
  const [hits, setHits] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const q = [parts.first, parts.last].filter(Boolean).join(' ').trim();
  useEffect(() => {
    if (!open || (parts.last ?? '').trim().length < 2) return setHits([]);
    const t = setTimeout(() => api.get<any>(`/api/reports/names?q=${encodeURIComponent(q)}`).then((r) => setHits(r.people)).catch(() => setHits([])), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);
  const box = (k: 'last' | 'first' | 'middle' | 'suffix', label: string, cls = '', ph = '') => (
    <label className={`rw-f ${cls}`}>
      <span>{label}</span>
      <input value={parts[k] ?? ''} onChange={(e) => (onPart(k, e.target.value), setOpen(true))} onBlur={() => setTimeout(() => setOpen(false), 200)} autoComplete="off" placeholder={ph} />
    </label>
  );
  return (
    <div className="rw-name">
      <div className="rw-names">
        {box('last', 'Last name', 'rw-n-last')}
        {box('first', 'First name', 'rw-n-first')}
        {box('middle', 'Middle', 'rw-n-mid')}
        {box('suffix', 'Suffix', 'rw-n-suf', 'Jr.')}
      </div>
      {open && hits.length > 0 && (
        <ul className="rw-hits" aria-label="People already in records">
          {hits.map((h) => (
            <li key={h.name_key}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => (onPick(h), setOpen(false))}>
                <strong>{h.name}</strong>
                <span className="small muted">
                  {[h.dob && `DOB ${h.dob}`, h.address, h.reports ? `${h.reports} report${h.reports === 1 ? '' : 's'}` : null, h.papers ? `${h.papers} civil paper${h.papers === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A small "Unknown" check under a field. */
function Unk({ on, onChange, what }: { on: boolean; onChange: (v: boolean) => void; what: string }) {
  return (
    <label className="rw-unk">
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} aria-label={`${what} unknown`} /> Unknown
    </label>
  );
}

export type PersonPart = 'all' | 'role' | 'name' | 'about' | 'contact' | 'physical' | 'victim' | 'arrest';

/** One person's fields. `part` shows just one group (one-question-at-a-time mode); 'all' shows the whole card body. */
export function PersonFields({ r, set, p, reportId, part = 'all' }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; p: ReportPerson; reportId?: string; part?: PersonPart }) {
  const upd = (f: (x: ReportPerson) => void) => set((x) => f(x.people.find((y) => y.id === p.id)!));
  const show = (k: PersonPart) => part === 'all' || part === k;
  const victim = isVictim(p);
  // Injury and relationship are asked for the offenses this victim is tied to (or every one until they're ticked).
  const mine = victim ? (offensesOfVictim(r, p).length ? offensesOfVictim(r, p) : victimOffenses(r)) : [];
  const injury = mine.some((o) => needsFor(o).injury);
  const relationship = mine.some((o) => needsFor(o).relationship);
  const suspects = r.people.filter(isSuspect).filter((s) => !s.unknown);
  const now = new Date();
  const business = isOrgVictim(p);
  const age = ageOn(p.dob, r.occurredFrom ? new Date(r.occurredFrom) : now);
  const unk = (t: string) => (p.unknownTraits ?? []).includes(t);
  const setUnk = (t: 'height' | 'weight' | 'hair' | 'eyes', on: boolean) =>
    upd((x) => {
      const cur = new Set(x.unknownTraits ?? []);
      if (on) {
        cur.add(t);
        if (t === 'height') x.height = null;
        if (t === 'weight') x.weight = null;
        if (t === 'hair') x.hair = null;
        if (t === 'eyes') x.eyes = null;
      } else cur.delete(t);
      x.unknownTraits = [...cur];
    });
  const setPart = (k: 'last' | 'first' | 'middle' | 'suffix', v: string) =>
    upd((x) => {
      Object.assign(x, nameParts(x));
      x[k] = v.trim() ? v : null;
      x.name = joinName(x);
    });
  return (
    <div className="stack sm">
      {show('role') && !p.unknown && (
        <Multi
          label="Role"
          max={4}
          options={(Object.keys(ROLE_LABELS) as PersonRole[]).map((k) => ({ code: k, label: ROLE_LABELS[k] }))}
          value={p.roles}
          onChange={(v) =>
            upd((x) => {
              x.roles = v as PersonRole[];
              if (v.includes('arrestee') && !x.arrest) x.arrest = newArrest();
            })
          }
        />
      )}
      {show('role') && victim && (
        <Sel
          label="The victim is"
          options={VICTIM_TYPES.filter((v) => v.code !== 'S' && (v.code !== 'L' || p.type === 'L' || r.offenses.some((o) => OFFICER_VICTIM_OFFENSES.includes(o.code))))}
          value={p.type || null}
          onChange={(v) =>
            upd((x) => {
              x.type = v ?? '';
              x.leoka = v === 'L' ? (x.leoka ?? { activity: null, assignment: null, ori: null }) : null;
            })
          }
        />
      )}
      {show('role') && victim && victimOffenses(r).length > 1 && (
        <Multi
          label="Victim of"
          hint="tick each offense against this victim"
          max={10}
          options={victimOffenses(r).map((o) => ({ code: o.id, label: OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code }))}
          value={(p.victimOf ?? []).filter((id) => victimOffenses(r).some((o) => o.id === id))}
          onChange={(v) => upd((x) => void (x.victimOf = v))}
        />
      )}
      {show('role') && victim && p.type === 'L' && (
        <div className="rw-row">
          <Sel label="What was the officer doing?" options={LEOKA_ACTIVITY} value={p.leoka?.activity ?? null} onChange={(v) => upd((x) => void (x.leoka = { ...(x.leoka ?? { activity: null, assignment: null, ori: null }), activity: v }))} />
          <Sel label="Officer’s assignment" options={LEOKA_ASSIGNMENT} value={p.leoka?.assignment ?? null} onChange={(v) => upd((x) => void (x.leoka = { ...(x.leoka ?? { activity: null, assignment: null, ori: null }), assignment: v }))} />
          <Txt label="Home agency ORI (if outside their jurisdiction)" value={p.leoka?.ori ?? ''} onChange={(v) => upd((x) => void (x.leoka = { ...(x.leoka ?? { activity: null, assignment: null, ori: null }), ori: v.toUpperCase() || null }))} placeholder="SC0120000" />
        </div>
      )}
      {p.unknown ? (
        show('name') || show('about') ? <Txt label="Description (what anyone saw)" wide value={p.description} onChange={(v) => upd((x) => void (x.description = v || null))} placeholder="Male, 20s, gray hoodie, left on foot toward Main St" /> : null
      ) : business ? (
        show('name') || show('contact') ? (
          <div className="rw-row">
            <Txt label="Business / organization name" wide value={p.name} onChange={(v) => upd((x) => void (x.name = v))} />
            <Txt label="Phone" value={p.phone} inputMode="tel" onChange={(v) => upd((x) => void (x.phone = v || null))} />
            <Txt label="Address" wide value={p.address} onChange={(v) => upd((x) => void (x.address = v || null))} />
          </div>
        ) : null
      ) : (
        <>
          {show('name') && (
            <>
              <LicenseScanButton
                small
                onScan={(d) =>
                  upd((x) => {
                    x.last = d.last;
                    x.first = d.first;
                    x.middle = d.middle;
                    x.suffix = d.suffix;
                    x.name = joinName(x);
                    x.dob = d.dob ?? x.dob;
                    x.sex = d.sex ?? x.sex;
                    x.address = d.address ?? x.address;
                    if (d.heightInches) x.height = d.heightInches;
                    if (d.weight) x.weight = d.weight;
                    const eye: Record<string, string> = { BRO: 'Brown', BLU: 'Blue', GRN: 'Green', HAZ: 'Hazel', GRY: 'Gray', BLK: 'Black' };
                    const hair: Record<string, string> = { BRO: 'Brown', BLK: 'Black', BLN: 'Blond', RED: 'Red', GRY: 'Gray', WHI: 'White', BAL: 'Bald' };
                    if (d.eyes && eye[d.eyes]) x.eyes = eye[d.eyes]!;
                    if (d.hair && hair[d.hair]) x.hair = hair[d.hair]!;
                  })
                }
              />
              <NameFields
                p={p}
                onPart={setPart}
                onPick={(h) =>
                  upd((x) => {
                    Object.assign(x, splitName(h.name));
                    x.name = joinName(x);
                    x.dob = h.dob ?? x.dob;
                    x.phone = h.phone ?? x.phone;
                    x.address = h.address ?? x.address;
                  })
                }
              />
              <div className="rw-row">
                <label className="rw-f">
                  <span>Date of birth {age != null && <strong>· age {age}</strong>}</span>
                  <input type="date" value={p.dob ?? ''} onChange={(e) => upd((x) => void (x.dob = e.target.value || null))} />
                </label>
                {!p.dob && <Txt label="Or about how old" value={p.age} inputMode="numeric" onChange={(v) => upd((x) => void (x.age = v || null))} placeholder="35, 30-35, or unknown" />}
              </div>
            </>
          )}
          {show('about') && (
            <div className="rw-row">
              <Choice label="Sex" options={SEXES} value={p.sex} onChange={(v) => upd((x) => void (x.sex = v))} />
              <Sel label="Race" options={RACES} value={p.race} onChange={(v) => upd((x) => void (x.race = v))} />
              <Sel label="Ethnicity" options={ETHNICITIES} value={p.ethnicity} onChange={(v) => upd((x) => void (x.ethnicity = v))} />
            </div>
          )}
          {show('contact') && (
            <div className="rw-row">
              <Txt label="Phone" value={p.phone} inputMode="tel" onChange={(v) => upd((x) => void (x.phone = v || null))} />
              <Txt label="Address" wide value={p.address} onChange={(v) => upd((x) => void (x.address = v || null))} />
            </div>
          )}
          {show('physical') && isSuspect(p) && (
            <>
              <div className="rw-row rw-phys">
                <div className="rw-f">
                  <span>Height</span>
                  <span className="rw-hgt">
                    <input aria-label="Height feet" inputMode="numeric" disabled={unk('height')} value={p.height != null ? Math.floor(p.height / 12) : ''} onChange={(e) => upd((x) => void (x.height = e.target.value === '' ? null : Number(e.target.value) * 12 + ((x.height ?? 0) % 12)))} placeholder="ft" />
                    <input aria-label="Height inches" inputMode="numeric" disabled={unk('height')} value={p.height != null ? p.height % 12 : ''} onChange={(e) => upd((x) => void (x.height = Math.floor((x.height ?? 0) / 12) * 12 + (Number(e.target.value) || 0)))} placeholder="in" />
                  </span>
                  <Unk what="Height" on={unk('height')} onChange={(v) => setUnk('height', v)} />
                </div>
                <div className="rw-f">
                  <label className="rw-f">
                    <span>Weight (lb)</span>
                    <input inputMode="numeric" disabled={unk('weight')} value={p.weight ?? ''} onChange={(e) => upd((x) => void (x.weight = e.target.value ? Number(e.target.value) : null))} />
                  </label>
                  <Unk what="Weight" on={unk('weight')} onChange={(v) => setUnk('weight', v)} />
                </div>
                <div className="rw-f">
                  <label className="rw-f">
                    <span>Hair</span>
                    <select disabled={unk('hair')} value={p.hair ?? ''} onChange={(e) => upd((x) => void (x.hair = e.target.value || null))}>
                      <option value="">Choose…</option>
                      {HAIR_COLORS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Unk what="Hair" on={unk('hair')} onChange={(v) => setUnk('hair', v)} />
                </div>
                <div className="rw-f">
                  <label className="rw-f">
                    <span>Eyes</span>
                    <select disabled={unk('eyes')} value={p.eyes ?? ''} onChange={(e) => upd((x) => void (x.eyes = e.target.value || null))}>
                      <option value="">Choose…</option>
                      {EYE_COLORS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Unk what="Eyes" on={unk('eyes')} onChange={(v) => setUnk('eyes', v)} />
                </div>
              </div>
              <Txt label="Scars, marks, tattoos" wide value={p.marks} onChange={(v) => upd((x) => void (x.marks = v || null))} placeholder="Tattoo “MOM” left forearm; scar over right eye" />
              <Txt label="Description (build, clothes)" wide value={p.description} onChange={(v) => upd((x) => void (x.description = v || null))} />
              {reportId && <Mugshot reportId={reportId} photoId={p.photoId} onChange={(pid) => upd((x) => void (x.photoId = pid))} />}
            </>
          )}
        </>
      )}
      {show('victim') && victim && !business && injury && <Multi label="Was the victim hurt?" max={5} options={INJURIES} value={p.injuries} onChange={(v) => upd((x) => void (x.injuries = v.includes('N') && v.length > 1 ? v.filter((y) => y !== 'N') : v))} />}
      {show('victim') &&
        victim &&
        !business &&
        relationship &&
        suspects
          .filter((s) => s.id !== p.id)
          .map((s) => (
            <Sel
              key={s.id}
              label={`The victim is ${personLabel(s)}’s…`}
              options={RELATIONSHIPS}
              value={p.relationships[s.id] ?? null}
              onChange={(v) =>
                upd((x) => {
                  if (v) x.relationships[s.id] = v;
                  else delete x.relationships[s.id];
                })
              }
            />
          ))}
      {show('arrest') && p.roles.includes('arrestee') && p.arrest && (
        <div className="rw-arrest">
          <div className="rw-ql">Arrest</div>
          <WhenField label="Arrested" value={p.arrest.at} onChange={(v) => upd((x) => void (x.arrest!.at = v))} now="Arrested now" />
          <div className="rw-row">
            <Sel
              label="Arrested for (the most serious offense)"
              options={[
                ...r.offenses
                  .filter((o) => {
                    const d = OFFENSE_BY_CODE.get(o.code);
                    return d && d.group !== 'NC' && !d.noArrest && (o.code !== '980' || o.attempted === true);
                  })
                  .map((o) => ({ code: o.code, label: OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code })),
                ...OFFENSES.filter((o) => o.group === 'B' && !r.offenses.some((x) => x.code === o.code)).map((o) => ({ code: o.code, label: o.plain })),
              ]}
              value={p.arrest.charge}
              onChange={(v) => upd((x) => void (x.arrest!.charge = v))}
            />
            <Txt label="Warrant # (if any)" value={p.arrest.warrant} onChange={(v) => upd((x) => void (x.arrest!.warrant = v || null))} />
          </div>
          <Choice label="How" options={ARREST_TYPES} value={p.arrest.type} onChange={(v) => upd((x) => void (x.arrest!.type = v))} />
          {p.arrest.charge === '35A' && (
            <div className="rw-row">
              <Sel label="Drug arrest: what for?" options={DRUG_ARREST_ACTIVITY} value={p.arrest.drugActivity} onChange={(v) => upd((x) => void (x.arrest!.drugActivity = v))} />
              <Sel label="Drug arrest: what drug?" options={ARREST_DRUG_TYPES} value={p.arrest.drugType} onChange={(v) => upd((x) => void (x.arrest!.drugType = v))} />
            </div>
          )}
          <Multi label="Armed with when arrested" hint="up to 2" max={2} options={ARRESTEE_ARMED} value={p.arrest.armed} onChange={(v) => upd((x) => void (x.arrest!.armed = v.includes('01') && v.length > 1 ? v.filter((y) => y !== '01') : v))} />
          {(() => {
            const ag = p.dob ? ageOn(p.dob, p.arrest!.at ? new Date(p.arrest!.at) : now) : p.age && /^\d+$/.test(p.age) ? Number(p.age) : null;
            return ag != null && ag < 18 ? <Choice label="Juvenile: what happened?" options={JUVENILE_DISPOSITION} value={p.arrest!.juvenile} onChange={(v) => upd((x) => void (x.arrest!.juvenile = v))} /> : null;
          })()}
          {p.arrest.type === 'T' && (
            <div className="rw-row">
              <Txt label="Booking #" value={p.arrest.bookingNumber} onChange={(v) => upd((x) => void (x.arrest!.bookingNumber = v || null))} />
              <Txt label="Jail / facility" wide value={p.arrest.facility} onChange={(v) => upd((x) => void (x.arrest!.facility = v || null))} placeholder="Chester County Detention Center" />
            </div>
          )}
          <div className="rw-ql">Charges as written (statute and words)</div>
          {p.arrest.localCharges.map((c, i) => (
            <div className="rw-row" key={i}>
              <Txt label="SC Code §" value={c.statute} onChange={(v) => upd((x) => void (x.arrest!.localCharges[i]!.statute = v))} placeholder="16-11-313" />
              <label className="rw-f wide">
                <span>Charge</span>
                <StatutePicker
                  value={c.description}
                  label="Charge"
                  placeholder="Type the charge or statute (burglary 2nd, 16-11-312)"
                  onText={(v) => upd((x) => void ((x.arrest!.localCharges[i]!.description = v), (x.arrest!.localCharges[i]!.cdr = null)))}
                  onPick={(pc) => upd((x) => void Object.assign(x.arrest!.localCharges[i]!, { description: chargeWords(pc.description), statute: pc.statute, cdr: pc.cdr }))}
                />
              </label>
              <Txt label="Warrant #" value={c.warrant} onChange={(v) => upd((x) => void (x.arrest!.localCharges[i]!.warrant = v || null))} />
              <button type="button" className="btn sm ghost" onClick={() => upd((x) => void x.arrest!.localCharges.splice(i, 1))}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn sm" onClick={() => upd((x) => void x.arrest!.localCharges.push({ statute: '', description: '', warrant: null }))}>
            + Add charge
          </button>
        </div>
      )}
    </div>
  );
}

/** The "add a person" buttons; returns the new person's id. */
export function addPerson(r: IncidentReport, set: (f: (r: IncidentReport) => void) => void, role: PersonRole, unknown = false): string {
  const p = { ...newPerson(uid('p'), role), unknown };
  if (role === 'arrestee') p.arrest = newArrest(); // time, type and charge are the deputy's to pick (SCIBRS: no defaults)
  set((x) => void x.people.push(p));
  return p.id;
}

export function AddPersonButtons({ r, set, onAdded }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; onAdded?: (id: string) => void }) {
  const add = (role: PersonRole, unknown = false) => {
    const id = addPerson(r, set, role, unknown);
    onAdded?.(id);
  };
  return (
    <div className="rw-add" role="group" aria-label="Add a person">
      <button type="button" className="btn" onClick={() => add('victim')}>
        + Victim
      </button>
      <button type="button" className="btn" onClick={() => add('suspect')}>
        + Suspect
      </button>
      <button type="button" className="btn" onClick={() => add('arrestee')}>
        + Arrested person
      </button>
      <button type="button" className="btn" onClick={() => add('witness')}>
        + Witness
      </button>
      <button type="button" className="btn" onClick={() => add('other')}>
        + Other
      </button>
      <PeopleFlags r={r} set={set} />
    </div>
  );
}

/** Offender unknown (adds the “Unknown suspect”) and victim is society / the public — two boxes instead of people. */
function PeopleFlags({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const unknown = r.people.some((p) => p.unknown && isSuspect(p));
  return (
    <div className="rw-flags">
      <label className="check">
        <input
          type="checkbox"
          checked={unknown}
          onChange={(e) =>
            e.target.checked ? void addPerson(r, set, 'suspect', true) : set((x) => void (x.people = x.people.filter((p) => !(p.unknown && p.roles.includes('suspect')))))
          }
        />{' '}
        Offender unknown
      </label>
      <label className="check">
        <input type="checkbox" checked={!!r.societyVictim} onChange={(e) => set((x) => void (x.societyVictim = e.target.checked))} /> Victim is society / the public
      </label>
    </div>
  );
}

export function PeopleStep({ r, set, reportId }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; reportId?: string }) {
  return (
    <div className="stack">
      <AddPersonButtons r={r} set={set} onAdded={(id) => setTimeout(() => document.getElementById(`ref-${id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50)} />
      {r.people.length === 0 && <p className="muted">Add the victim, the suspect (or tick “Offender unknown”), anyone arrested, and witnesses.</p>}
      {r.people.map((p) => (
        <Card
          key={p.id}
          id={p.id}
          tone={p.roles.includes('arrestee') ? 'arrest' : isVictim(p) ? 'victim' : isSuspect(p) ? 'suspect' : ''}
          title={<>{personLabel(p)} <span className="muted small">{p.roles.map((x) => ROLE_LABELS[x]).join(' · ')}</span></>}
          onRemove={() => set((x) => void (x.people = x.people.filter((y) => y.id !== p.id)))}
        >
          <PersonFields r={r} set={set} p={p} reportId={reportId} />
        </Card>
      ))}
    </div>
  );
}

/** Mugshot: take or pick a photo; kept with the report. */
function Mugshot({ reportId, photoId, onChange }: { reportId: string; photoId: string | null; onChange: (id: string | null) => void }) {
  const a = useAction();
  return (
    <div className="rw-mug">
      {photoId ? <img src={`/api/reports/photos/${photoId}`} alt="Mugshot" /> : <div className="rw-mug-empty">No photo</div>}
      <div className="stack sm">
        <label className="btn sm">
          {photoId ? 'Retake photo' : '📷 Take / add photo'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            aria-label="Mugshot photo"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f)
                a.run(async () => {
                  const r = await api.post<{ id: string }>(`/api/reports/${reportId}/photos`, { kind: 'mugshot', imageBase64: await photoToJpegBase64(f) });
                  onChange(r.id);
                });
            }}
          />
        </label>
        {a.busy && <span className="small muted">Uploading…</span>}
        <ErrorBox error={a.error} />
      </div>
    </div>
  );
}

// ─── Step 4: Property ───────────────────────────────────────────────────────

export function PropertyStep({ r, set, hideNeeds }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; hideNeeds?: boolean }) {
  const upd = (id: string, f: (p: ReportProperty) => void) => set((x) => f(x.property.find((p) => p.id === id)!));
  const add = (loss: string, type = '') => {
    const p = { ...newProperty(uid('i'), loss), type };
    if (type === '10') p.drug = { type: null, qty: null, unit: null };
    set((x) => void x.property.push(p));
    setTimeout(() => document.getElementById(`ref-${p.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  };
  const needs = hideNeeds ? [] : r.offenses.map((o) => ({ o, n: needsFor(o) })).filter((x) => x.n.property);
  const total = (loss: string) => r.property.filter((p) => p.loss === loss).reduce((s, p) => s + (p.value ?? 0), 0);
  return (
    <div className="stack">
      {needs.length > 0 && (
        <Notice tone="info">
          {needs.map(({ n }, i) => (
            <Fragment key={i}>
              {i > 0 && ' · '}
              {n.def!.plain}: add {n.property!.ask}.
            </Fragment>
          ))}
        </Notice>
      )}
      <div className="rw-add" role="group" aria-label="Add property">
        <button type="button" className="btn" onClick={() => add('7')}>
          + Stolen
        </button>
        <button type="button" className="btn" onClick={() => add('4')}>
          + Damaged
        </button>
        <button type="button" className="btn" onClick={() => add('5')}>
          + Recovered
        </button>
        <button type="button" className="btn" onClick={() => add('6')}>
          + Seized / evidence
        </button>
        <button type="button" className="btn" onClick={() => add('6', '10')}>
          + Drugs seized
        </button>
        <button type="button" className="btn" onClick={() => add('7', '03')}>
          + Stolen vehicle
        </button>
        <button type="button" className="btn" onClick={() => add('1')} title="For an attempt, a break-in where nothing was taken, or a drug charge where nothing was seized">
          + None (nothing taken or seized)
        </button>
      </div>
      {r.property.length === 0 && <p className="muted">No property. Add what was stolen, damaged, recovered or seized.</p>}
      {r.property.map((p) => {
        const vehicle = VEHICLE_PROPERTY.includes(p.type);
        const none = p.loss === '1';
        const drug = p.type === '10' && p.loss === '6';
        const drugCharge = r.offenses.some((o) => o.code === '35A');
        return (
          <Card key={p.id} id={p.id} title={<>{p.description || labelOf(PROPERTY_TYPES, p.type) || 'Item'} <span className="muted small">{labelOf(LOSS_TYPES, p.loss)}</span></>} onRemove={() => set((x) => void (x.property = x.property.filter((y) => y.id !== p.id)))}>
            <div className="stack sm">
              <Choice label="What happened to it?" options={LOSS_TYPES.filter((l) => l.code !== '8')} value={p.loss || null} onChange={(v) => upd(p.id, (x) => void (x.loss = v ?? ''))} />
              {none ? (
                <div className="stack sm">
                  <p className="small muted">“None” tells the state nothing was taken or seized (an attempt, a break-in where nothing was taken, or a drug charge with no drugs seized).</p>
                  {drugCharge && <Sel label="Suspected drug (required for a drug charge with nothing seized)" options={DRUG_TYPES} value={p.drug?.type ?? null} onChange={(v) => upd(p.id, (x) => void (x.drug = { type: v, qty: null, unit: null }))} />}
                </div>
              ) : (
              <>
              <div className="rw-row">
                <Sel label="What is it?" options={PROPERTY_TYPES} value={p.type || null} onChange={(v) => upd(p.id, (x) => ((x.type = v ?? ''), (x.drug = v === '10' ? x.drug ?? { type: null, qty: null, unit: null } : null)))} />
                <Txt label="Description" wide value={p.description} onChange={(v) => upd(p.id, (x) => void (x.description = v))} placeholder={drug ? 'Clear baggie, white crystals' : 'Red Craftsman push mower'} />
              </div>
              {drug ? (
                <div className="rw-row">
                  <Sel label="Drug" options={DRUG_TYPES} value={p.drug?.type ?? null} onChange={(v) => upd(p.id, (x) => void (x.drug = { ...(x.drug ?? { qty: null, unit: null }), type: v }))} />
                  {p.drug?.type !== 'X' && (
                    <>
                      <Txt label="Amount" type="number" inputMode="decimal" value={p.drug?.qty ?? null} onChange={(v) => upd(p.id, (x) => void (x.drug = { ...(x.drug ?? { type: null, unit: null }), qty: v ? Number(v) : null }))} />
                      <Sel label="Unit" options={DRUG_UNITS} value={p.drug?.unit ?? null} onChange={(v) => upd(p.id, (x) => void (x.drug = { ...(x.drug ?? { type: null, qty: null }), unit: v }))} />
                    </>
                  )}
                </div>
              ) : (
                <div className="rw-row">
                  <label className="rw-f">
                    <span>Value $</span>
                    <input type="number" inputMode="decimal" min={0} value={p.value ?? ''} disabled={p.valueUnknown} onChange={(e) => upd(p.id, (x) => void (x.value = e.target.value === '' ? null : Number(e.target.value)))} />
                  </label>
                  <label className="check small rw-unk">
                    <input type="checkbox" checked={p.valueUnknown} onChange={(e) => upd(p.id, (x) => ((x.valueUnknown = e.target.checked), e.target.checked && (x.value = null)))} /> Value unknown
                  </label>
                  <Txt label="How many" type="number" inputMode="numeric" value={p.quantity} onChange={(v) => upd(p.id, (x) => void (x.quantity = v ? Number(v) : null))} />
                  {p.loss === '5' && (
                    <label className="rw-f">
                      <span>Date recovered</span>
                      <input type="date" value={p.recoveredOn ?? ''} onChange={(e) => upd(p.id, (x) => void (x.recoveredOn = e.target.value || null))} />
                    </label>
                  )}
                </div>
              )}
              {!drug && (
                <div className="rw-row">
                  {vehicle && <Txt label="Year" value={p.year} inputMode="numeric" onChange={(v) => upd(p.id, (x) => void (x.year = v || null))} />}
                  <Txt label="Make" value={p.make} onChange={(v) => upd(p.id, (x) => void (x.make = v || null))} />
                  <Txt label="Model" value={p.model} onChange={(v) => upd(p.id, (x) => void (x.model = v || null))} />
                  <Txt label="Color" value={p.color} onChange={(v) => upd(p.id, (x) => void (x.color = v || null))} />
                  {vehicle ? (
                    <>
                      <Txt label="Tag" value={p.plate} onChange={(v) => upd(p.id, (x) => void (x.plate = v.toUpperCase() || null))} />
                      <Txt label="VIN" value={p.vin} onChange={(v) => upd(p.id, (x) => void (x.vin = v.toUpperCase() || null))} />
                    </>
                  ) : (
                    <Txt label="Serial #" value={p.serial} onChange={(v) => upd(p.id, (x) => void (x.serial = v || null))} />
                  )}
                </div>
              )}
              <Txt label="Owner (if not the victim)" wide value={p.owner} onChange={(v) => upd(p.id, (x) => void (x.owner = v || null))} />
              <label className="check rw-ev">
                <input type="checkbox" checked={p.evidence} onChange={(e) => upd(p.id, (x) => void (x.evidence = e.target.checked))} /> Book into evidence <span className="small muted">(gets a barcode number when the report is sent)</span>
              </label>
              </>
              )}
            </div>
          </Card>
        );
      })}
      {r.property.length > 0 && (
        <p className="small muted">
          Stolen: <strong>${total('7').toLocaleString()}</strong> · Damaged: <strong>${total('4').toLocaleString()}</strong> · Recovered: <strong>${total('5').toLocaleString()}</strong>
        </p>
      )}
    </div>
  );
}

// ─── Step 5: Narrative ──────────────────────────────────────────────────────

export function NarrativeStep({ r, set }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void }) {
  const words = r.narrative.trim() ? r.narrative.trim().split(/\s+/).length : 0;
  return (
    <div className="stack">
      <section className="rw-card">
        <header>
          <h3>Narrative</h3>
          <span className="small muted">{words} words</span>
        </header>
        <textarea
          className="rw-narr"
          aria-label="Narrative"
          value={r.narrative}
          onChange={(e) => set((x) => void (x.narrative = e.target.value))}
          placeholder={'On (date) at (time) I responded to (address) in reference to …\n\nWhat you saw and did, what each person told you, evidence collected, and what happens next.'}
        />
      </section>
      <section className="rw-card">
        <header>
          <h3>Case status</h3>
        </header>
        <Choice
          label="Where does the case stand?"
          options={[
            { code: 'open', label: 'Open / under investigation' },
            { code: 'arrest', label: 'Cleared by arrest' },
            { code: 'exceptional', label: 'Cleared exceptionally' },
            { code: 'closed', label: 'Closed, not cleared (administrative)' },
            { code: 'unfounded', label: 'Unfounded (didn’t happen)' },
          ]}
          value={r.clearance.status}
          onChange={(v) => set((x) => void (x.clearance.status = (v ?? 'open') as IncidentReport['clearance']['status']))}
        />
        {r.clearance.status === 'exceptional' && (
          <div className="rw-row">
            <Sel label="Why" options={EXCEPTIONAL_CLEARANCE} value={r.clearance.reason} onChange={(v) => set((x) => void (x.clearance.reason = v))} />
            <label className="rw-f">
              <span>Date cleared</span>
              <input type="date" value={r.clearance.date ?? ''} onChange={(e) => set((x) => void (x.clearance.date = e.target.value || null))} />
            </label>
          </div>
        )}
        <Txt label="Related case / call numbers (optional)" wide value={r.related} onChange={(v) => set((x) => void (x.related = v || null))} />
      </section>
    </div>
  );
}

// ─── Review ─────────────────────────────────────────────────────────────────

function ReviewStep({ data, issues, goFix, onDone }: { data: any; issues: ReportIssue[]; goFix: (i: ReportIssue) => void; onDone: () => void }) {
  const sups = useLoad(() => (data.canSubmit ? api.get<any>('/api/reports/approvers') : Promise.resolve({ approvers: [] })), [data.canSubmit]);
  const [sendTo, setSendTo] = useState<string>(() => lastReviewer());
  useEffect(() => {
    // Forget a remembered supervisor who's no longer on the list.
    if (sups.data && sendTo && !sups.data.approvers.some((u: any) => u.id === sendTo)) setSendTo('');
  }, [sups.data, sendTo]);
  const r: IncidentReport = data.report;
  const { navigate } = useRouter();
  const a = useAction();
  const [note, setNote] = useState('');
  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');
  const victims = r.people.filter(isVictim);
  const suspects = r.people.filter(isSuspect);
  return (
    <div className="stack">
      {data.canEdit || data.canSubmit ? (
        errors.length ? (
          <section className="rw-card rw-fix">
            <header>
              <h3>
                {errors.length} thing{errors.length === 1 ? '' : 's'} to finish
              </h3>
            </header>
            <ul className="rw-issues">
              {errors.map((i, k) => (
                <li key={k}>
                  <span>{i.message}</span>
                  <button type="button" className="btn sm" onClick={() => goFix(i)}>
                    Fix
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <Notice tone="ok">Everything the state needs is filled in.</Notice>
        )
      ) : null}
      {warnings.length > 0 && (data.canEdit || data.canSubmit || data.canReview) && (
        <section className="rw-card">
          <header>
            <h3>Worth a look</h3>
          </header>
          <ul className="rw-issues warn">
            {warnings.map((i, k) => (
              <li key={k}>
                <span>{i.message}</span>
                {data.canEdit && (
                  <button type="button" className="btn sm ghost" onClick={() => goFix(i)}>
                    Go
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="ops rw-photos">
        <PhotoFolders incidentId={data.incident.id} embedded />
      </div>
      <section className="rw-card rw-summary">
        <header>
          <h3>Report {data.incident.caseNumber}</h3>
          <span className="row gap-sm">
            {data.incident.status !== 'draft' && (
              <button type="button" className="btn sm" onClick={() => navigate(`${location.pathname.startsWith('/field') ? '/field' : ''}/cases/${data.incident.id}`)}>
                Follow this case
              </button>
            )}
            {data.incident.status !== 'draft' && (
              <button type="button" className="btn sm" onClick={() => navigate(`${reportsBase()}/similar?from=${data.incident.id}`)}>
                Find similar cases
              </button>
            )}
            <button type="button" className="btn sm ghost" onClick={() => window.open(`${reportsBase()}/${data.incident.id}/print`, '_blank')}>
              Print
            </button>
          </span>
        </header>
        <dl className="kv">
          <dt>Offenses</dt>
          <dd>{r.offenses.map((o) => `${OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code}${o.attempted ? ' (attempted)' : ''}`).join(', ') || '—'}</dd>
          <dt>When</dt>
          <dd>
            {r.occurredFrom ? fmtDateTime(r.occurredFrom) : '—'}
            {r.occurredTo ? ` to ${fmtDateTime(r.occurredTo)}` : ''}
          </dd>
          <dt>Where</dt>
          <dd>
            {[r.location.line1, r.location.city].filter(Boolean).join(', ') || '—'}
            {r.location.type ? ` · ${labelOf(LOCATION_TYPES, r.location.type)}` : ''}
            {r.location.detail ? ` · ${r.location.detail}` : ''}
          </dd>
          <dt>Victims</dt>
          <dd>{victims.map(personLabel).join(', ') || '—'}</dd>
          <dt>Suspects</dt>
          <dd>{suspects.map(personLabel).join(', ') || '—'}</dd>
          <dt>Property</dt>
          <dd>{r.property.map((p) => `${p.description || labelOf(PROPERTY_TYPES, p.type)} (${labelOf(LOSS_TYPES, p.loss).toLowerCase()}${p.value != null ? `, $${p.value.toLocaleString()}` : ''})`).join('; ') || '—'}</dd>
          {data.evidence?.length > 0 && (
            <>
              <dt>Evidence</dt>
              <dd>
                <ul className="plain">
                  {data.evidence.map((e: any) => (
                    <li key={e.id}>
                      <button type="button" className="btn link mono" onClick={() => navigate(`${evidenceBase()}/${e.id}`)}>
                        {e.number}
                      </button>{' '}
                      {e.description} · <span className="muted small">{EVIDENCE_STATUS_LABEL[e.status as keyof typeof EVIDENCE_STATUS_LABEL]}{e.location ? `, ${e.location}` : ''}</span>
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn sm" onClick={() => window.open(`${evidenceBase()}/labels?incident=${data.incident.id}`, '_blank')}>
                  Print evidence labels
                </button>
              </dd>
            </>
          )}
          {r.offenses.some((o) => o.code === INFO_ONLY) && (
            <>
              <dt>Report type</dt>
              <dd>Information only — no crime, no charges</dd>
            </>
          )}
          {(r.societyVictim || r.people.some((p) => p.unknown && isSuspect(p))) && (
            <>
              <dt>Also</dt>
              <dd>{[r.societyVictim && 'Victim is society / the public', r.people.some((p) => p.unknown && isSuspect(p)) && 'Offender unknown'].filter(Boolean).join(' · ')}</dd>
            </>
          )}
          {r.narcan?.used && (
            <>
              <dt>Narcan</dt>
              <dd>
                Used{r.narcan.doses ? ` (${r.narcan.doses} dose${r.narcan.doses === 1 ? '' : 's'})` : ''} — {NARCAN_GIVERS.find((g) => g.code === r.narcan!.givenBy)?.label.toLowerCase() ?? 'who gave it not said'}
                {r.narcan.givenBy === 'me' && (r.narcan.formDone ? ' · Narcan form completed' : ' · Narcan form not done yet')}
              </dd>
            </>
          )}
          <dt>Status</dt>
          <dd>{r.clearance.status === 'open' ? 'Open' : r.clearance.status === 'arrest' ? 'Cleared by arrest' : r.clearance.status === 'exceptional' ? `Cleared exceptionally — ${labelOf(EXCEPTIONAL_CLEARANCE, r.clearance.reason)}` : r.clearance.status === 'closed' ? 'Closed, not cleared' : 'Unfounded'}</dd>
        </dl>
        <div className="rw-ql">Narrative</div>
        <p className="rw-narr-view">{r.narrative || '—'}</p>
      </section>
      <PassOnBox incidentId={data.incident.id} passOn={data.passOn} />

      {data.canSubmit && (
        <div className="rw-actions rw-send">
          <label className="rw-f">
            <span>Send to</span>
            <select value={sendTo} onChange={(e) => (setSendTo(e.target.value), rememberReviewer(e.target.value))} aria-label="Send to supervisor">
              <option value="">Any supervisor</option>
              {(sups.data?.approvers ?? []).map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn primary big"
            disabled={a.busy || errors.length > 0}
            onClick={() =>
              a.run(async () => {
                await api.post(`/api/reports/${data.incident.id}/submit`, sendTo ? { reviewerId: sendTo } : {});
                onDone();
              })
            }
          >
            Send for approval
          </button>
          {errors.length > 0 && <span className="small muted">Finish the {errors.length} item{errors.length === 1 ? '' : 's'} above first.</span>}
        </div>
      )}
      {data.canReview && (
        <section className="rw-card rw-reviewbox">
          <header>
            <h3>Supervisor review</h3>
            <span className="small muted">Written by {data.incident.authorName}</span>
          </header>
          <label className="rw-f wide">
            <span>If it needs fixing, say what (goes back to the deputy)</span>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add the serial number for the mower; victim’s DOB is missing…" />
          </label>
          <div className="rw-review-actions">
            <button
              type="button"
              className="btn"
              disabled={a.busy || note.trim().length < 3}
              onClick={() =>
                a.run(async () => {
                  await api.post(`/api/reports/${data.incident.id}/return`, { note: note.trim() });
                  onDone();
                })
              }
            >
              Send back to {data.incident.authorName}
            </button>
            <span className="spacer" />
            {errors.length > 0 && <span className="small muted">{errors.length} item{errors.length === 1 ? '' : 's'} missing</span>}
            <button
              type="button"
              className="btn primary"
              disabled={a.busy || errors.length > 0}
              onClick={() =>
                a.run(async () => {
                  await api.post(`/api/reports/${data.incident.id}/approve`);
                  onDone();
                })
              }
            >
              Approve
            </button>
          </div>
        </section>
      )}
      <ErrorBox error={a.error} />
      {(data.supplements?.length > 0 || data.canSupplement) && (
        <section className="rw-card">
          <header>
            <h3>Supplements</h3>
            {data.canSupplement && (
              <button
                type="button"
                className="btn primary"
                disabled={a.busy}
                onClick={() =>
                  a.run(async () => {
                    const x = await api.post<{ id: string }>(`/api/reports/${data.incident.id}/supplements`);
                    navigate(`${reportsBase()}/supplements/${x.id}`);
                  })
                }
              >
                + Add supplement
              </button>
            )}
          </header>
          {data.supplements?.length ? (
            <ul className="rp-list">
              {data.supplements.map((x: any) => (
                <li key={x.id}>
                  <button type="button" onClick={() => navigate(`${reportsBase()}/supplements/${x.id}`)}>
                    <div className="rp-main">
                      <strong>Supplement {x.number}</strong>
                      <span className="small muted">
                        {x.author_name}
                        {x.approved_at ? ` · approved ${fmtDateTime(x.approved_at)}` : ''}
                      </span>
                      {x.status === 'returned' && x.return_note && <span className="small rp-note">“{x.return_note}”</span>}
                    </div>
                    <div className="rp-side">
                      <Badge tone={STATUS_TONE[x.status] ?? 'neutral'}>{STATUS_LABEL[x.status] ?? x.status}</Badge>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="small muted">Follow-up, an arrest, property recovered, or the case cleared? Add a supplement. The approved report stays as it was.</p>
          )}
        </section>
      )}
      {data.events?.length > 0 && (
        <section className="rw-card print-only">
          <header>
            <h3>History</h3>
          </header>
          <ul className="plain small">
            {data.events.map((e: any, i: number) => (
              <li key={i}>
                {fmtDateTime(e.at)} — {e.action} by {e.by_name}
                {e.note ? `: “${e.note}”` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── The wizard ─────────────────────────────────────────────────────────────

const REVIEWER_KEY = 'ch-last-reviewer';
const lastReviewer = () => {
  try {
    return localStorage.getItem(REVIEWER_KEY) ?? '';
  } catch {
    return '';
  }
};
const rememberReviewer = (id: string) => {
  try {
    localStorage.setItem(REVIEWER_KEY, id);
  } catch {
    /* fine */
  }
};

// ─── One question at a time ─────────────────────────────────────────────────

type ReportMode = 'cards' | 'form' | 'narrative';
const MODE_KEY = 'ch-report-mode';
export function getReportMode(): ReportMode | null {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return v === 'cards' || v === 'form' || v === 'narrative' ? v : null;
  } catch {
    return null;
  }
}
function saveReportMode(m: ReportMode) {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    /* private window: keep it for this visit only */
  }
}

interface Question {
  id: string;
  step: Step;
  title: string;
  help?: string;
  done: boolean;
  body: ReactNode;
}

/** The questions for this report, in order. People add questions as they're added. */
function questionsFor(r: IncidentReport, set: (f: (r: IncidentReport) => void) => void, reportId: string, onAdded: (id: string) => void): Question[] {
  const qs: Question[] = [
    { id: 'what', step: 'what', title: 'What happened?', help: 'Describe it in plain words or type an offense code. Add everything that happened — a break-in where something was taken is two.', done: r.offenses.length > 0 && !checkReport(r, new Date()).some((i) => i.level === 'error' && i.step === 'what'), body: <WhatStep r={r} set={set} /> },
    { id: 'happened', step: 'where', title: 'When did it happen?', done: !!r.occurredFrom || r.occurredUnknown === 'date', body: <HappenedFields r={r} set={set} /> },
    { id: 'reported', step: 'where', title: 'When was it reported?', done: !!r.reportedAt, body: <ReportedFields r={r} set={set} /> },
    { id: 'address', step: 'where', title: 'Where did it happen?', done: !!r.location.line1.trim(), body: <AddressFields r={r} set={set} /> },
    { id: 'place', step: 'where', title: 'What kind of place was it?', done: !!r.location.type, body: <PlaceFields r={r} set={set} /> },
    {
      id: 'people',
      step: 'people',
      title: 'Who was involved?',
      help: 'Add the victim, the suspect (or tick “Offender unknown”), anyone arrested, and witnesses. Each one gets a few quick questions next.',
      done: r.people.length > 0 || !!r.societyVictim || r.offenses.some((o) => o.code === INFO_ONLY),
      body: (
        <div className="stack sm">
          <AddPersonButtons r={r} set={set} onAdded={onAdded} />
          {r.people.length > 0 && (
            <ul className="qc-people">
              {r.people.map((p) => (
                <li key={p.id}>
                  <span>
                    <strong>{personLabel(p)}</strong> <span className="muted small">{p.roles.map((x) => ROLE_LABELS[x]).join(' · ')}</span>
                  </span>
                  <button type="button" className="btn sm ghost" onClick={() => set((x) => void (x.people = x.people.filter((y) => y.id !== p.id)))}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
    },
  ];
  for (const p of r.people) {
    const who = p.roles.map((x) => ROLE_LABELS[x].toLowerCase()).join(' / ') || 'person';
    const label = p.name?.trim() ? personLabel(p) : `the ${who}`;
    const body = (part: PersonPart) => <PersonFields r={r} set={set} p={p} reportId={reportId} part={part} />;
    const business = isOrgVictim(p);
    if (p.unknown) {
      qs.push({ id: `name-${p.id}`, step: 'people', title: 'Describe the unknown suspect', help: 'Anything anyone saw: sex, age, clothes, which way they went.', done: !!p.description, body: body('name') });
      continue;
    }
    qs.push({ id: `role-${p.id}`, step: 'people', title: `What was ${label}’s part in this?`, help: 'Pick all that fit.', done: p.roles.length > 0 && (!isVictim(p) || (!!p.type && (victimOffenses(r).length < 2 || (p.victimOf ?? []).length > 0))), body: body('role') });
    qs.push({ id: `name-${p.id}`, step: 'people', title: business ? 'Business name, phone and address' : `What’s the ${who}’s name?`, done: !!p.name.trim(), body: body('name') });
    if (business) continue;
    qs.push({ id: `about-${p.id}`, step: 'people', title: `${label}: sex, race and ethnicity`, done: !!p.sex && !!p.race, body: body('about') });
    qs.push({ id: `contact-${p.id}`, step: 'people', title: `${label}: phone and address`, done: !!p.phone || !!p.address, body: body('contact') });
    if (isSuspect(p)) qs.push({ id: `physical-${p.id}`, step: 'people', title: `What does ${label} look like?`, help: 'Check “Unknown” for anything you don’t know.', done: (p.height != null || p.unknownTraits.includes('height')) && (p.weight != null || p.unknownTraits.includes('weight')), body: body('physical') });
    if (isVictim(p) && (offensesOfVictim(r, p).length ? offensesOfVictim(r, p) : victimOffenses(r)).some((o) => needsFor(o).injury || needsFor(o).relationship)) qs.push({ id: `victim-${p.id}`, step: 'people', title: `About ${label} as the victim`, done: true, body: body('victim') });
    if (p.roles.includes('arrestee')) qs.push({ id: `arrest-${p.id}`, step: 'people', title: `Arrest of ${label}`, done: !!p.arrest?.type && !!p.arrest?.charge, body: body('arrest') });
  }
  qs.push({ id: 'property', step: 'property', title: 'Was anything taken, damaged, recovered or seized?', help: 'Add each item. If nothing, just go on.', done: true, body: <PropertyStep r={r} set={set} /> });
  qs.push({ id: 'narrative', step: 'narrative', title: 'In your own words, what happened?', done: r.narrative.trim().length >= 20, body: <NarrativeStep r={r} set={set} /> });
  return qs;
}

function CardFlow({ r, set, reportId, data, issues, flush, goFixForm, onDone, onClose, save }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; reportId: string; data: any; issues: ReportIssue[]; flush: () => void; goFixForm: (i: ReportIssue) => void; onDone: () => void; onClose: () => void; save: string }) {
  const [qid, setQid] = useState<string>(() => sessionStorage.getItem(`ch-q-${reportId}`) ?? 'what');
  const [skipped, setSkipped] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(`ch-skip-${reportId}`) ?? '[]');
    } catch {
      return [];
    }
  });
  const [addedId, setAddedId] = useState<string | null>(null);
  const qs = questionsFor(r, set, reportId, setAddedId);
  const atReview = qid === 'review';
  let i = qs.findIndex((q) => q.id === qid);
  if (i < 0 && !atReview) i = 0;
  const q = atReview ? null : qs[i]!;
  useEffect(() => {
    try {
      sessionStorage.setItem(`ch-q-${reportId}`, qid);
      sessionStorage.setItem(`ch-skip-${reportId}`, JSON.stringify(skipped));
    } catch {
      /* fine */
    }
  }, [qid, skipped, reportId]);
  const goTo = (id: string) => (flush(), setQid(id), window.scrollTo({ top: 0 }));
  const next = (skip: boolean) => {
    if (!q) return;
    setSkipped((cur) => (skip ? [...new Set([...cur, q.id])] : cur.filter((x) => x !== q.id)));
    // After adding someone on "Who was involved?", go straight to their questions.
    if (q.id === 'people' && addedId && qs.some((x) => x.id === `role-${addedId}` || x.id === `name-${addedId}`)) {
      const first = qs.find((x) => x.id === `role-${addedId}`) ?? qs.find((x) => x.id === `name-${addedId}`)!;
      setAddedId(null);
      return goTo(first.id);
    }
    goTo(qs[i + 1]?.id ?? 'review');
  };
  const back = () => goTo(atReview ? qs.at(-1)!.id : qs[i - 1]?.id ?? qs[0]!.id);
  const answered = qs.filter((x) => x.done).length;
  const skippedQs = qs.filter((x) => skipped.includes(x.id) && !x.done);
  const pct = atReview ? 100 : Math.round((i / qs.length) * 100);
  const stepLabel = (s: Step) => STEPS.find((x) => x.id === s)?.label ?? '';
  return (
    <div className="qc-wrap">
      <div className="qc-top">
        <div className="qc-prog" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="How far along">
          <i style={{ width: `${pct}%` }} />
        </div>
        <div className="qc-count small muted">
          {atReview ? 'Review & send' : `Question ${i + 1} of ${qs.length} · ${stepLabel(q!.step)}`}
          <span className="spacer" />
          {answered} answered{skippedQs.length ? ` · ${skippedQs.length} skipped` : ''}
        </div>
      </div>
      {q ? (
        <section className="qc" aria-labelledby="qc-title" key={q.id}>
          <h2 id="qc-title" className="qc-title">
            {q.title}
          </h2>
          {q.help && <p className="qc-help">{q.help}</p>}
          <fieldset className="rw-body qc-body">{q.body}</fieldset>
          <footer className="qc-foot">
            <button type="button" className="btn" onClick={back} disabled={i === 0}>
              Back
            </button>
            <span className="spacer" />
            <span className={`rw-save ${save}`} aria-live="polite">
              {save === 'saved' ? '✓ Saved' : save === 'saving' ? 'Saving…' : save === 'dirty' ? '' : save === 'conflict' ? 'Changed elsewhere — reload' : 'Not saved — will retry'}
            </span>
            <button type="button" className="btn ghost qc-skip" onClick={() => next(true)}>
              Skip for now
            </button>
            <button type="button" className="btn primary qc-next" onClick={() => next(false)}>
              {i === qs.length - 1 ? 'Review' : 'Next'}
            </button>
          </footer>
        </section>
      ) : (
        <div className="stack">
          {skippedQs.length > 0 && (
            <section className="rw-card qc-skipped">
              <header>
                <h3>You skipped {skippedQs.length === 1 ? 'one question' : `${skippedQs.length} questions`}</h3>
              </header>
              <ul className="plain">
                {skippedQs.map((x) => (
                  <li key={x.id}>
                    <button type="button" className="btn link" onClick={() => goTo(x.id)}>
                      {x.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <ReviewStep data={{ ...data, report: r }} issues={issues} goFix={goFixForm} onDone={onDone} />
          <div className="row gap">
            <button type="button" className="btn" onClick={back}>
              Back
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Save & close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** First time a deputy writes a report: pick how. They can switch any time. */
function ModePicker({ onPick }: { onPick: (m: ReportMode) => void }) {
  return (
    <section className="qc-pick" aria-label="How do you want to write reports?">
      <h2>How do you want to write reports?</h2>
      <p className="muted">You can switch any time from the top of the report.</p>
      <div className="qc-pick-opts">
        <button type="button" onClick={() => onPick('cards')}>
          <span className="qc-pick-art" aria-hidden>
            <i />
          </span>
          <strong>One question at a time</strong>
          <span>Big, simple screens. Answer, tap Next, or skip and come back. Good on a phone.</span>
        </button>
        <button type="button" onClick={() => onPick('form')}>
          <span className="qc-pick-art form" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <strong>Full form</strong>
          <span>Every section on one screen with a checklist on the side. Good at a desk.</span>
        </button>
        <button type="button" onClick={() => onPick('narrative')}>
          <span className="qc-pick-art narr" aria-hidden>
            <i />
            <i />
          </span>
          <strong>Narrative first</strong>
          <span>Write what happened first. BlueHQ picks out the offense, address, times, people and property for you to add.</span>
        </button>
      </div>
    </section>
  );
}

/** What each section card on the full form shows: the gist of what's in it, or what's still to do. */
function SectionSummary({ step, r, errors, editable }: { step: Step; r: IncidentReport; errors: number; editable: boolean }) {
  const none = (t: string) => <span className="rec-card-l muted">{t}</span>;
  if (step === 'what') {
    if (!r.offenses.length) return none('Nothing picked yet');
    return (
      <>
        {r.offenses.slice(0, 3).map((o) => (
          <span key={o.id} className="rec-card-l">
            {OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code}
          </span>
        ))}
        {r.offenses.length > 3 && none(`+ ${r.offenses.length - 3} more`)}
      </>
    );
  }
  if (step === 'where') {
    const where = [r.location.line1, r.location.city].filter(Boolean).join(', ');
    const when = r.occurredFrom ? fmtStamp(r.occurredFrom) + (r.occurredTo ? ` – ${fmtStamp(r.occurredTo)}` : '') : r.occurredUnknown === 'date' ? 'Date unknown' : '';
    return (
      <>
        {where ? <span className="rec-card-l">{where}</span> : none('No address yet')}
        {when ? <span className="rec-card-l">{when}</span> : none('No time yet')}
      </>
    );
  }
  if (step === 'people') {
    const n = (f: (p: ReportPerson) => boolean) => r.people.filter(f).length;
    const rows: [string, number][] = [
      ['Victims', n(isVictim)],
      ['Suspects', n(isSuspect)],
      ['Witnesses', n((p) => p.roles.includes('witness'))],
      ['Complainants', n((p) => p.roles.includes('complainant'))],
    ];
    const arrested = n((p) => p.roles.includes('arrestee'));
    if (arrested) rows.push(['Arrested', arrested]);
    return (
      <span className="rec-card-counts">
        {rows.map(([l, c]) => (
          <span key={l} className={c ? '' : 'muted'}>
            {l} <b>{c}</b>
          </span>
        ))}
      </span>
    );
  }
  if (step === 'property') {
    if (!r.property.length) return none('Nothing added');
    const ev = r.property.filter((p) => p.evidence).length;
    return (
      <>
        {r.property.slice(0, 2).map((p) => (
          <span key={p.id} className="rec-card-l">
            {p.description || labelOf(PROPERTY_TYPES, p.type) || 'Item'}
          </span>
        ))}
        {none(`${r.property.length} item${r.property.length === 1 ? '' : 's'}${ev ? ` · ${ev} evidence` : ''}`)}
      </>
    );
  }
  if (step === 'narrative') {
    const t = r.narrative.trim();
    if (!t) return none('Not written yet');
    return <span className="rec-card-l rec-card-narr">{t.length > 110 ? `${t.slice(0, 110)}…` : t}</span>;
  }
  return editable ? none(errors ? `${errors} item${errors === 1 ? '' : 's'} to finish before sending` : 'Ready to send') : none('Read the whole report');
}

const HINT_LABEL: Record<NarrativeHint['kind'], string> = { offense: 'What happened', address: 'Location', time: 'Time', person: 'Person', property: 'Property taken' };
const HINT_ROLES: { code: PersonRole; label: string }[] = [
  { code: 'victim', label: 'Victim' },
  { code: 'suspect', label: 'Suspect' },
  { code: 'witness', label: 'Witness' },
  { code: 'complainant', label: 'Complainant' },
];

/** Narrative first: write what happened; add what BlueHQ picks out of it; then finish the rest on the full form. */
function NarrativeFirst({ r, set, save, onContinue }: { r: IncidentReport; set: (f: (r: IncidentReport) => void) => void; save: string; onContinue: (s: Step) => void }) {
  const [text, setText] = useState(r.narrative);
  const [roles, setRoles] = useState<Record<string, PersonRole>>({});
  const [dismissed, setDismissed] = useState<string[]>([]);
  // Look for things a moment after typing stops, not on every key.
  const [scan, setScan] = useState(r.narrative);
  useEffect(() => {
    const t = setTimeout(() => setScan(text), 500);
    return () => clearTimeout(t);
  }, [text]);
  const hints = useMemo(() => narrativeHints(scan), [scan]);
  const localTime = (iso: string) => toLocalInput(iso).slice(11, 16);
  const shown = hints.filter((h) => !dismissed.includes(h.key));
  const add = (h: NarrativeHint) =>
    set((x) => {
      if (h.kind === 'offense') {
        if (!x.offenses.some((o) => o.code === h.code)) x.offenses = [...x.offenses.filter((o) => o.code !== INFO_ONLY), newOffense(uid('o'), h.code)];
      } else if (h.kind === 'address') {
        x.location.line1 = h.line1;
        if (h.city) x.location.city = h.city;
      } else if (h.kind === 'time') {
        const date = h.date ?? toLocalInput(new Date().toISOString()).slice(0, 10);
        const iso = fromLocalInput(`${date}T${h.time}`);
        if (!x.occurredFrom) x.occurredFrom = iso;
        else if (!x.occurredTo && iso && iso > x.occurredFrom) x.occurredTo = iso;
        else x.occurredFrom = iso;
      } else if (h.kind === 'person') {
        const role = roles[h.key] ?? h.role;
        const p = newPerson(uid('p'), role);
        p.name = h.name;
        Object.assign(p, splitName(h.name));
        x.people.push(p);
      } else if (h.kind === 'property') {
        const p = newProperty(uid('pr'), '7');
        p.description = h.description;
        x.property.push(p);
      }
    });
  const onType = (v: string) => {
    setText(v);
    set((x) => void (x.narrative = v));
  };
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const what = (h: NarrativeHint) =>
    h.kind === 'offense' ? h.label : h.kind === 'address' ? [h.line1, h.city].filter(Boolean).join(', ') : h.kind === 'time' ? h.label : h.kind === 'person' ? h.name : h.description;
  return (
    <div className="nf">
      <section className="nf-write">
        <header>
          <h2>Tell what happened</h2>
          <span className="small muted">
            {words} words · <span className={`rw-save ${save}`}>{save === 'saved' ? '✓ Saved' : save === 'saving' ? 'Saving…' : save === 'dirty' ? 'Editing…' : 'Not saved — will retry'}</span>
          </span>
        </header>
        <p className="small muted">Write it like you would on paper. BlueHQ picks out the offense, address, times, people and stolen property for you to add with one tap — it can miss things, so check them.</p>
        <textarea
          className="nf-text"
          aria-label="Narrative"
          autoFocus={!r.narrative}
          value={text}
          onChange={(e) => onType(e.target.value)}
          placeholder={'On (date) at (time) I responded to (address) in reference to …\n\nWhat you saw and did, what each person told you, what was taken, evidence collected, and what happens next.'}
        />
        <div className="nf-go">
          <button type="button" className="btn primary" onClick={() => onContinue('what')}>
            Finish the rest of the report
          </button>
        </div>
      </section>
      <aside className="nf-side" aria-label="Found in your narrative">
        <h3>Found in your narrative</h3>
        {!shown.length ? (
          <p className="small muted">{text.trim().length < 40 ? 'Start writing. Addresses, times, names and what was taken show up here.' : 'Nothing picked out yet. You can add everything on the full form.'}</p>
        ) : (
          <ul className="nf-hints">
            {shown.map((h) => {
              const done = hintApplied(h, r, localTime);
              return (
                <li key={h.key} className={done ? 'done' : ''}>
                  <span className={`nf-k k-${h.kind}`}>{HINT_LABEL[h.kind]}</span>
                  <span className="nf-v">{what(h)}</span>
                  {h.kind === 'offense' && <span className="small muted">from “{h.because}”</span>}
                  <span className="nf-act">
                    {done ? (
                      <span className="nf-added">Added ✓</span>
                    ) : (
                      <>
                        {h.kind === 'person' && (
                          <select aria-label={`${h.name}: role`} value={roles[h.key] ?? h.role} onChange={(e) => setRoles({ ...roles, [h.key]: e.target.value as PersonRole })}>
                            {HINT_ROLES.map((o) => (
                              <option key={o.code} value={o.code}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        )}
                        <button type="button" className="btn sm primary" onClick={() => add(h)}>
                          Add
                        </button>
                        <button type="button" className="btn sm ghost" aria-label={`Not this: ${what(h)}`} onClick={() => setDismissed([...dismissed, h.key])}>
                          ✕
                        </button>
                      </>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <h3>On the report</h3>
        <ul className="nf-on">
          {STEPS.filter((s) => s.id !== 'review' && s.id !== 'narrative').map((s) => (
            <li key={s.id}>
              <button type="button" className="btn link" onClick={() => onContinue(s.id)}>
                {s.label}
              </button>
              <span className="small">
                {s.id === 'what'
                  ? r.offenses.map((o) => OFFENSE_BY_CODE.get(o.code)?.plain ?? o.code).join(', ') || '—'
                  : s.id === 'where'
                    ? [r.location.line1, r.occurredFrom ? fmtStamp(r.occurredFrom) : null].filter(Boolean).join(' · ') || '—'
                    : s.id === 'people'
                      ? r.people.map((p) => `${personLabel(p)} (${p.roles.map((x) => ROLE_LABELS[x].toLowerCase()).join(', ')})`).join(', ') || '—'
                      : r.property.map((p) => p.description || 'item').join(', ') || '—'}
              </span>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

export function ReportWizard({ id }: { id: string }) {
  const { navigate } = useRouter();
  const [data, setData] = useState<any>(null);
  const [report, setReport] = useState<IncidentReport | null>(null);
  const [issues, setIssues] = useState<ReportIssue[]>([]);
  const [step, setStep] = useState<Step>(() => (new URLSearchParams(location.search).get('step') as Step) || 'what');
  // Steps the deputy has opened and moved on from (an empty people or property step then counts as done).
  const [seen, setSeen] = useState<Set<Step>>(() => new Set());
  const [save, setSave] = useState<'saved' | 'saving' | 'dirty' | 'conflict' | 'error'>('saved');
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [mode, setModeState] = useState<ReportMode | null>(() => getReportMode());
  const setMode = (m: ReportMode) => (saveReportMode(m), setModeState(m));
  const version = useRef(0);
  const pending = useRef<IncidentReport | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saving = useRef(false);

  const load = () =>
    api
      .get<any>(`/api/reports/${id}`)
      .then((d) => {
        setData(d);
        setReport(d.report);
        setIssues(d.issues);
        version.current = d.incident.version;
        setSave('saved');
        if (!d.canEdit || d.canReview) setStep('review');
      })
      .catch(setLoadErr);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const flush = async () => {
    if (saving.current || !pending.current) return;
    saving.current = true;
    const r = pending.current;
    pending.current = null;
    setSave('saving');
    try {
      const res = await api.post<{ version: number; issues: ReportIssue[] }>(`/api/reports/${id}/save`, { report: r, version: version.current });
      version.current = res.version;
      setIssues(res.issues);
      setSave(pending.current ? 'dirty' : 'saved');
    } catch (e) {
      setSave(e instanceof ApiError && e.status === 409 ? 'conflict' : 'error');
      if (!(e instanceof ApiError)) pending.current = pending.current ?? r; // offline: keep it and try again
    } finally {
      saving.current = false;
      if (pending.current) timer.current = setTimeout(flush, 600);
    }
  };
  // Leaving the page with unsaved changes: save right away.
  useEffect(() => {
    const h = () => void flush();
    window.addEventListener('pagehide', h);
    return () => {
      window.removeEventListener('pagehide', h);
      void flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (f: (r: IncidentReport) => void) => {
    setReport((cur) => {
      const next = structuredClone(cur!);
      f(next);
      pending.current = next;
      return next;
    });
    setSave('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };

  const goFix = (i: ReportIssue) => {
    setStep(i.step);
    if (i.ref) {
      setTimeout(() => {
        const el = document.getElementById(`ref-${i.ref}`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el.classList.add('flash');
          setTimeout(() => el.classList.remove('flash'), 1600);
        }
      }, 60);
    } else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loadErr) return <ErrorBox error={loadErr} />;
  if (!data || !report) return <Spinner />;
  const errorsFor = (s: Step) => (s === 'review' ? 0 : issues.filter((i) => i.level === 'error' && i.step === s).length);
  const countFor = (s: Step) => (s === 'what' ? report.offenses.length : s === 'people' ? report.people.length : s === 'property' ? report.property.length : 0);
  const idx = STEPS.findIndex((s) => s.id === step);
  const editable = data.canEdit;
  const done = () => navigate(reportsBase());
  const errors = issues.filter((i) => i.level === 'error');
  const dataSteps = STEPS.filter((s) => s.id !== 'review');
  // A step counts once it's done: nothing missing, and something in it. People and property can rightly be empty,
  // so an empty one counts once the deputy has looked at it, or once the parts every report needs are done.
  const required = dataSteps.filter((s) => s.id !== 'people' && s.id !== 'property');
  const requiredDone = required.every((s) => !errorsFor(s.id) && (s.id !== 'what' || report.offenses.length));
  const stepDone = (s: Step) => !errorsFor(s) && (s === 'what' ? report.offenses.length > 0 : s === 'people' || s === 'property' ? countFor(s) > 0 || seen.has(s) || requiredDone : true);
  const title = report.offenses.length ? (OFFENSE_BY_CODE.get(report.offenses[0]!.code)?.name ?? report.offenses[0]!.code) + (report.offenses.length > 1 ? ` + ${report.offenses.length - 1} more` : '') : 'New incident';
  const groupA = report.offenses.filter((o) => OFFENSE_BY_CODE.get(o.code)?.group === 'A').length;
  const go = (s: Step) => (void flush(), setSeen((x) => new Set(x).add(step)), setStep(s), window.scrollTo({ top: 0 }));
  const shown = !editable && data.current ? data.current : report;
  const when = (iso: string | null) => (iso ? fmtStamp(iso) : '—');
  return (
    <div className={`rw rec${editable && !data.canReview && mode === 'cards' ? ' rw-cards' : ''}`}>
      <header className="rec-head">
        <div className="crumb">
          <button type="button" className="btn link" onClick={() => (void flush(), navigate(reportsBase()))}>
            Incident reports
          </button>{' '}
          ›
        </div>
        <div className="rec-title">
          <h1>
            <span className="mono">{data.incident.caseNumber}</span> <span className="rec-sep">—</span> {title}
          </h1>
          <Badge tone={STATUS_TONE[data.incident.status] ?? 'neutral'} dot>
            {STATUS_LABEL[data.incident.status] ?? data.incident.status}
          </Badge>
          {save === 'conflict' && (
            <button className="btn sm" onClick={load}>
              Reload
            </button>
          )}
          {editable && !data.canReview && mode && (
            <div className="seg-ctl sm rw-mode" role="group" aria-label="How to write this report">
              <button type="button" className={mode === 'cards' ? 'on' : ''} aria-pressed={mode === 'cards'} onClick={() => (void flush(), setMode('cards'))}>
                One at a time
              </button>
              <button type="button" className={mode === 'form' ? 'on' : ''} aria-pressed={mode === 'form'} onClick={() => (void flush(), setMode('form'))}>
                Full form
              </button>
              <button type="button" className={mode === 'narrative' ? 'on' : ''} aria-pressed={mode === 'narrative'} onClick={() => (void flush(), setMode('narrative'))}>
                Narrative first
              </button>
            </div>
          )}
        </div>
        {!(editable && !data.canReview && data.incident.status === 'draft') && (
        <dl className="rec-meta">
          <div>
            <dt>Reporting officer</dt>
            <dd>{data.incident.authorName}</dd>
          </div>
          <div>
            <dt>Occurred</dt>
            <dd>
              {when(shown.occurredFrom)}
              {shown.occurredTo ? ` – ${when(shown.occurredTo)}` : ''}
            </dd>
          </div>
          <div>
            <dt>Reported</dt>
            <dd>{when(shown.reportedAt)}</dd>
          </div>
          <div>
            <dt>Location</dt>
            <dd>{[shown.location.line1, shown.location.city].filter(Boolean).join(', ') || '—'}</dd>
          </div>
          <div>
            <dt>State reporting</dt>
            <dd>{report.offenses.length ? (groupA ? `Group A, ${report.offenses.length} offense${report.offenses.length === 1 ? '' : 's'}` : 'Not reportable') : '—'}</dd>
          </div>
          {data.incident.status === 'submitted' && data.incident.reviewerName && (
            <div>
              <dt>Sent to</dt>
              <dd>{data.incident.reviewerName}</dd>
            </div>
          )}
          {data.incident.approvedBy && (
            <div>
              <dt>Approved</dt>
              <dd>
                {data.incident.approvedBy}, {when(data.incident.approvedAt)}
              </dd>
            </div>
          )}
        </dl>
        )}
      </header>
      {data.incident.status === 'returned' && data.incident.returnNote && (
        <Notice tone="warn">
          <strong>Returned by your supervisor:</strong> {data.incident.returnNote}
        </Notice>
      )}
      {editable && !data.canReview && mode === null && <ModePicker onPick={setMode} />}
      {editable && !data.canReview && mode === 'cards' && (
        <CardFlow
          r={report}
          set={set}
          reportId={id}
          data={data}
          issues={issues}
          save={save}
          flush={() => void flush()}
          goFixForm={(i) => (setMode('form'), goFix(i))}
          onDone={done}
          onClose={() => (void flush(), navigate(reportsBase()))}
        />
      )}
      {editable && !data.canReview && mode === 'narrative' && (
        <NarrativeFirst r={report} set={set} save={save} onContinue={(s) => (void flush(), setMode('form'), setStep(s), window.scrollTo({ top: 0 }))} />
      )}
      {!(editable && !data.canReview && mode !== 'form') && (
      <>
      <div className="rec-grid cards">
        <nav className="rec-cards" aria-label="Report sections">
          {STEPS.map((s) => {
            const n = errorsFor(s.id);
            const complete = s.id !== 'review' && stepDone(s.id);
            const tone = s.id === 'review' ? (errors.length ? '' : ' ok') : n ? ' bad' : complete ? ' ok' : '';
            return (
              <button key={s.id} type="button" className={`rec-card${s.id === step ? ' on' : ''}${tone}${s.id === 'review' ? ' review' : ''}`} aria-current={s.id === step ? 'step' : undefined} onClick={() => go(s.id)}>
                <span className="rec-card-h">
                  <span className="rec-card-t">{s.label}</span>
                  {s.id !== 'review' && (n ? <span className="rec-card-s bad">{n} to fix</span> : complete ? <span className="rec-card-s ok">✓</span> : null)}
                </span>
                <SectionSummary step={s.id} r={report} errors={errors.length} editable={editable} />
              </button>
            );
          })}
          {data.events?.length > 1 && (
            <section className="rec-card-act">
              <h4>Activity</h4>
              <ul className="rec-act">
                {data.events.slice(-6).map((e: any, k: number) => (
                  <li key={k} className={`ev-${e.action}`}>
                    <span className="rec-act-a">{EVENT_LABEL[e.action] ?? e.action}</span>
                    <span className="rec-act-t">
                      {fmtStamp(e.at)} · {e.by_name}
                    </span>
                    {e.note && <span className={`rec-act-n${e.action === 'returned' ? '' : ' plain'}`}>{e.action === 'returned' ? `“${e.note}”` : e.note}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </nav>
        <div className="rec-main">
          <fieldset className="rw-body" disabled={!editable && step !== 'review'}>
            {step === 'what' && <WhatStep r={report} set={set} />}
            {step === 'where' && <WhereStep r={report} set={set} />}
            {step === 'people' && <PeopleStep r={report} set={set} reportId={id} />}
            {step === 'property' && <PropertyStep r={report} set={set} />}
            {step === 'narrative' && <NarrativeStep r={report} set={set} />}
          </fieldset>
          {step === 'review' && <ReviewStep data={{ ...data, report: shown }} issues={issues} goFix={goFix} onDone={done} />}
        </div>
      </div>
      {step !== 'review' && (
        <footer className="rw-nav rec-foot">
          {editable ? (
            <span className={`rw-save ${save}`} aria-live="polite">
              {save === 'saved' ? '✓ Saved' : save === 'saving' ? 'Saving…' : save === 'dirty' ? 'Editing…' : save === 'conflict' ? 'Changed elsewhere — reload' : 'Not saved — will retry'}
            </span>
          ) : (
            <span className="muted small">Read only</span>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={() => (void flush(), navigate(reportsBase()))}>
            {editable ? 'Save & close' : 'Close'}
          </button>
          {idx > 0 && (
            <button type="button" className="btn" onClick={() => go(STEPS[idx - 1]!.id)}>
              Back
            </button>
          )}
          <button type="button" className="btn primary" onClick={() => go(STEPS[idx + 1]!.id)}>
            Next: {STEPS[idx + 1]!.label}
          </button>
        </footer>
      )}
      </>
      )}
    </div>
  );
}
